import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import axios from 'axios';

// Chart components
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell 
} from 'recharts';

const API_BASE_URL = "https://uman.onrender.com/api";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    verifiedUsers: 0,
    usersOverTime: [],
    socialDistribution: [],
    verificationLevels: [],
    referrals: [],
    tokenStats: {},
    friendDistribution: [],
    notifications: {}
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

  useEffect(() => {
    const fetchAllStats = async () => {
      setLoading(true);
      try {
        // Fetch all stats in parallel
        const [
          totalUsersRes,
          verifiedUsersRes,
          usersOverTimeRes,
          socialDistributionRes,
          tokenStatsRes,
          friendDistributionRes,
          notificationsRes
        ] = await Promise.all([
          axios.get(`${API_BASE_URL}/stats/users/total`),
          axios.get(`${API_BASE_URL}/stats/users/verified`),
          axios.get(`${API_BASE_URL}/stats/users/created?period=day&days=30`),
          axios.get(`${API_BASE_URL}/stats/users/social-links-distribution`),
          axios.get(`${API_BASE_URL}/stats/users/token-balance-stats`),
          axios.get(`${API_BASE_URL}/stats/users/friend-count-distribution`),
          axios.get(`${API_BASE_URL}/stats/users/notifications`)
        ]);

        // Process and format the data for charts
        const processedSocialDistribution = socialDistributionRes.data.success ? 
          socialDistributionRes.data.distribution.map(item => {
            // Create more meaningful labels
            let name = '';
            if (item._id === 0) name = 'No linked accounts';
            else if (item._id === 1) name = '1 linked account';
            else name = `${item._id} linked accounts`;
            
            return {
              name: name,
              value: item.count
            };
          }) : [];



        const processedFriendDistribution = friendDistributionRes.data.success ?
          friendDistributionRes.data.distribution.map(item => ({
            name: `${item._id} Friend${item._id !== 1 ? 's' : ''}`,
            value: item.count
          })) : [];

        // Update the stats state
        setStats({
          totalUsers: totalUsersRes.data.success ? totalUsersRes.data.totalUsers : 0,
          verifiedUsers: verifiedUsersRes.data.success ? verifiedUsersRes.data.verifiedUsers : 0,
          usersOverTime: usersOverTimeRes.data.success ? usersOverTimeRes.data.data.map(item => ({
            date: item._id,
            users: item.count
          })) : [],
          socialDistribution: processedSocialDistribution,
          tokenStats: tokenStatsRes.data.success ? tokenStatsRes.data.stats : {},
          friendDistribution: processedFriendDistribution,
          notifications: notificationsRes.data.success ? notificationsRes.data : {}
        });
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching stats:", err);
        setError("Failed to load statistics. Please try again later.");
        setLoading(false);
      }
    };

    fetchAllStats();
  }, []);

  if (loading) {
    return <div className="dashboard-loading">Loading statistics...</div>;
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  const verificationPercentage = stats.totalUsers > 0 
    ? ((stats.verifiedUsers / stats.totalUsers) * 100).toFixed(1) 
    : 0;

  const notificationsPercentage = stats.notifications.total > 0
    ? ((stats.notifications.enabled / stats.notifications.total) * 100).toFixed(1)
    : 0;

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-title">UMAN Statistics Dashboard</h1>
      
      {/* Summary Cards */}
      <div className="stats-summary">
        <div className="stat-card">
          <h3>Total Users</h3>
          <p className="stat-value">{stats.totalUsers.toLocaleString()}</p>
        </div>
        
        <div className="stat-card">
          <h3>Verified Users</h3>
          <p className="stat-value">{stats.verifiedUsers.toLocaleString()}</p>
          <p className="stat-subtext">{verificationPercentage}% of all users</p>
        </div>
        
        <div className="stat-card">
          <h3>Notification Opt-ins</h3>
          <p className="stat-value">{stats.notifications.enabled?.toLocaleString() || 0}</p>
          <p className="stat-subtext">{notificationsPercentage}% of all users</p>
        </div>
      </div>
      
      {/* Users Over Time Chart */}
      <div className="chart-container">
        <h2>User Sign-ups (Last 30 Days)</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={stats.usersOverTime}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="users" stroke="#8884d8" activeDot={{ r: 8 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Social Distribution Pie Chart */}
      <div className="chart-container">
        <h2>Social Accounts Distribution</h2>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={stats.socialDistribution}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              {stats.socialDistribution.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
      

      
      {/* Token Stats */}
      <div className="chart-container">
        <h2>Token Balance Statistics</h2>
        {stats.tokenStats.average ? (
          <div className="token-stats">
            <div className="token-stat-item">
              <h3>Average</h3>
              <p>{stats.tokenStats.average.toFixed(2)}</p>
            </div>
            <div className="token-stat-item">
              <h3>Minimum</h3>
              <p>{stats.tokenStats.min}</p>
            </div>
            <div className="token-stat-item">
              <h3>Maximum</h3>
              <p>{stats.tokenStats.max}</p>
            </div>
          </div>
        ) : (
          <p className="no-data">No token data available</p>
        )}
      </div>
      
      {/* Friend Distribution */}
      <div className="chart-container">
        <h2>Friend Count Distribution</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={stats.friendDistribution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" fill="#ffc658" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Dashboard;
